import React, {useMemo} from "react";
import {Calendar, dateFnsLocalizer, View} from "react-big-calendar";
import {format, parse, startOfWeek, getDay} from "date-fns";
import {enUS} from "date-fns/locale";
import {Box, Paper, Typography, useTheme, useMediaQuery} from "@mui/material";
import {FilterList as FilterIcon} from "@mui/icons-material";
import {ChevronLeft, ChevronRight} from "lucide-react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../../styles/SessionCalendar.css";

// Custom startOfWeek function to start on Monday (1) instead of Sunday (0)
const startOfWeekMonday = (date: Date) => startOfWeek(date, { weekStartsOn: 1 });

// Set up the localizer for date-fns
const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: startOfWeekMonday,
  getDay,
  locales,
});

interface SessionEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: any; // Full session data
}

interface SessionCalendarProps {
  events: SessionEvent[];
  onSelectEvent?: (event: SessionEvent) => void;
  onSelectSlot?: (slotInfo: any) => void;
  loading?: boolean;
  defaultView?: View;
  defaultDate?: Date;
  onToggleFilters?: () => void;
  filtersExpanded?: boolean;
  onShowMore?: (events: SessionEvent[], date: Date) => void;
  currentUserId?: string;
}

const SessionCalendar: React.FC<SessionCalendarProps> = ({
  events,
  onSelectEvent,
  onSelectSlot,
  loading = false,
  defaultView = "month",
  defaultDate,
  onToggleFilters,
  filtersExpanded = false,
  onShowMore,
  currentUserId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Event style getter for custom styling - matching design system
  const eventStyleGetter = useMemo(() => {
    return (event: SessionEvent) => {
      const session = event.resource;

      // Check if current user is participating in this session
      const isParticipating =
        currentUserId &&
        session?.players?.some((p: any) => p.playerId === currentUserId);

      // Check if session is full (at capacity)
      const isFull = session?.players?.length >= (session?.maxPlayers || 6);

      // Use CSS custom properties for consistent theming
      let backgroundColor = "var(--main)";
      let borderColor = "var(--dark)";

      // User-based color variants using design system
      if (session?.status === "completed") {
        backgroundColor = "#6b7280"; // Gray for completed
        borderColor = "#4b5563";
      } else if (isParticipating) {
        backgroundColor = "#f59e0b"; // Amber for sessions user is signed up for
        borderColor = "#d97706";
      } else if (isFull) {
        backgroundColor = "#6b7280"; // Grey for full sessions
        borderColor = "#4b5563";
      } else {
        backgroundColor = "#22c55e"; // Green for available sessions
        borderColor = "#16a34a";
      }

      const style = {
        backgroundColor,
        borderRadius: "4px",
        color: "white",
        border: `1px solid ${borderColor}`,
        display: "block",
        fontSize: "12px",
        fontWeight: "500",
        fontFamily: "'Inter', Arial, system-ui, sans-serif",
        padding: "4px 8px",
        cursor: "pointer",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease-out",
      };

      return {style};
    };
  }, [currentUserId]);

  // Custom components for better MUI integration
  const components = useMemo(
    () => ({
      toolbar: (props: any) => (
        <Box sx={{mb: 2}}>
          {isMobile ? (
            // Mobile layout
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
              {/* Top row: Month/Week toggle and Filter button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Left: Month/Week toggle */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <button
                    onClick={() => props.onView("month")}
                    className={`btn btn-secondary ${props.view === "month" ? "calendar-btn-active" : ""}`}
                    style={{
                      borderRadius: "20px 0 0 20px",
                      borderRight: "none",
                    }}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => props.onView("week")}
                    className={`btn btn-secondary ${props.view === "week" ? "calendar-btn-active" : ""}`}
                    style={{
                      borderRadius: "0 20px 20px 0",
                      borderLeft: "none",
                    }}
                  >
                    Week
                  </button>
                </Box>

                {/* Right: Filter button */}
                {onToggleFilters && (
                  <button
                    onClick={onToggleFilters}
                    className={`btn btn-secondary ${filtersExpanded ? "calendar-btn-active" : ""}`}
                    title={filtersExpanded ? "Hide Filters" : "Show Filters"}
                  >
                    <FilterIcon sx={{fontSize: 18, mr: 0.5}} />
                    Filters
                  </button>
                )}
              </Box>

              {/* Bottom row: Month/Year display and navigation */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Left: Month/Year display */}
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    color: "var(--dark)",
                    textAlign: "left",
                  }}
                >
                  {props.label}
                </Typography>

                {/* Right: Navigation buttons and Today button */}
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                  <button
                    onClick={() => props.onNavigate("PREV")}
                    className="calendar-nav-icon-button"
                    title="Previous"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => props.onNavigate("NEXT")}
                    className="calendar-nav-icon-button"
                    title="Next"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <button
                    onClick={() => props.onNavigate("TODAY")}
                    className="btn btn-secondary"
                    style={{marginLeft: 8}}
                  >
                    Today
                  </button>
                </Box>
              </Box>
            </Box>
          ) : (
            // Desktop layout
            <Box sx={{mb: 2}}>
              {/* Top row: Month/Week toggle and Filter button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  gap: 2,
                }}
              >
                {/* Left: Month/Week toggle */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <button
                    onClick={() => props.onView("month")}
                    className={`btn btn-secondary ${props.view === "month" ? "calendar-btn-active" : ""}`}
                    style={{
                      borderRadius: "20px 0 0 20px",
                      borderRight: "none",
                    }}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => props.onView("week")}
                    className={`btn btn-secondary ${props.view === "week" ? "calendar-btn-active" : ""}`}
                    style={{
                      borderRadius: "0 20px 20px 0",
                      borderLeft: "none",
                    }}
                  >
                    Week
                  </button>
                </Box>

                {/* Right: Filter button (replacing weather item) */}
                {onToggleFilters && (
                  <button
                    onClick={onToggleFilters}
                    className={`btn btn-secondary ${filtersExpanded ? "calendar-btn-active" : ""}`}
                    title={filtersExpanded ? "Hide Filters" : "Show Filters"}
                  >
                    <FilterIcon sx={{fontSize: 18, mr: 0.5}} />
                    Filters
                  </button>
                )}
              </Box>

              {/* Bottom row: Month/Year display and navigation */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Left: Month/Year display */}
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    color: "var(--dark)",
                    minWidth: "160px",
                    textAlign: "left",
                  }}
                >
                  {props.label}
                </Typography>

                {/* Right: Navigation buttons and Today button */}
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                  <button
                    onClick={() => props.onNavigate("PREV")}
                    className="calendar-nav-icon-button"
                    title="Previous"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => props.onNavigate("NEXT")}
                    className="calendar-nav-icon-button"
                    title="Next"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <button
                    onClick={() => props.onNavigate("TODAY")}
                    className="btn btn-secondary"
                    style={{marginLeft: 8}}
                  >
                    Today
                  </button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      ),
    }),
    [onToggleFilters, filtersExpanded, isMobile]
  );

  if (loading) {
    return (
      <Paper sx={{p: 3, textAlign: "center"}}>
        <Typography variant="h6">Loading calendar...</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{p: 2}}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{height: "900px"}}
        onSelectEvent={onSelectEvent}
        onSelectSlot={onSelectSlot}
        selectable
        eventPropGetter={eventStyleGetter}
        components={components}
        defaultView={defaultView}
        defaultDate={defaultDate}
        views={["month", "week"]}
        step={60}
        tooltipAccessor="title"
        onShowMore={onShowMore}
      />
    </Paper>
  );
};

export default SessionCalendar;
